import { useMemo } from "react";

import type { Me } from "@/app/session/me";
import { useSessionUser } from "@/app/session/useSession";

/** A permission key exactly as the API's middleware checks it ("lead.view", "cold-call.assign"). */
export type Permission = string;

export type RoleName = "admin" | "team_leader" | "agent";

const NO_PERMISSIONS: readonly Permission[] = [];

/** The keys a person holds: direct and role permissions, as GET /me lists them. */
export function permissionsOf(
  user: Pick<Me, "all_permissions"> | undefined,
): ReadonlySet<Permission> {
  return new Set(user?.all_permissions ?? NO_PERMISSIONS);
}

export function usePermissions(): ReadonlySet<Permission> {
  const user = useSessionUser();
  const list = user?.all_permissions ?? NO_PERMISSIONS;
  return useMemo(() => new Set(list), [list]);
}

/** Whether `permissions` allow `required`: no requirement means yes; a list means any of them. */
export function allows(
  permissions: ReadonlySet<Permission>,
  required: Permission | readonly Permission[] | undefined,
): boolean {
  if (required === undefined) return true;
  if (typeof required === "string") return permissions.has(required);
  return required.some((key) => permissions.has(key));
}

/**
 * Whether the signed-in person may use something: a nav entry, a button, a screen. The server
 * checks every request; this only hides what the server would refuse.
 */
export function useCan(required?: Permission | readonly Permission[]): boolean {
  return allows(usePermissions(), required);
}

/** The person's built-in role, for role-shaped screens (the dashboard). Custom roles map to agent. */
export function primaryRole(user: Pick<Me, "roles"> | undefined): RoleName {
  const roles = user?.roles ?? [];
  if (roles.includes("admin")) return "admin";
  if (roles.includes("team_leader")) return "team_leader";
  return "agent";
}

export function useRole(): RoleName {
  return primaryRole(useSessionUser());
}
